import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import { CalendarDays, Stethoscope, FileText, Repeat, Phone, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const journey = [
 {
 icon: CalendarDays,
 n: "01",
 title: "Book in minutes",
 desc: "Choose a date and time that fit your week. No phone tag, no maze of forms, just a few quick fields and you're set.",
 detail: "We send a confirmation right away with everything you need to prepare. If you have insurance, we'll verify your coverage before your visit.",
 },
 {
 icon: Stethoscope,
 n: "02",
 title: "Meet your psychiatrist",
 desc: "A 50-minute virtual evaluation focused entirely on you. We listen first, ask careful questions, and explain every step in plain language.",
 detail: "By the end of your first visit, you'll have a clear diagnostic impression and a starting plan, never a rushed handoff.",
 },
 {
 icon: FileText,
 n: "03",
 title: "Receive a personalised plan",
 desc: "Whether it includes medication, therapy referrals, lifestyle adjustments, or all three, the plan is yours, written in language that makes sense.",
 detail: "We'll talk through trade-offs, alternatives, and timelines so you can make decisions that feel right.",
 },
 {
 icon: Repeat,
 n: "04",
 title: "Follow-up & ongoing care",
 desc: "Stability is built between visits. Expect a predictable cadence of follow-ups, secure messaging in between, and adjustments as life changes.",
 detail: "If you ever need more support, we coordinate referrals to therapy, PRP, MAT, or crisis services, with warm handoffs, not cold lists.",
 },
];

export default function HowItWorks() {
 return (
 <SiteLayout>
 <SEO
 title="How Our Online Care Works"
 description="From first booking to your ongoing follow-ups, here's exactly how virtual psychiatric care with Golden Life Standard Health unfolds — no surprises, no waiting rooms."
 path="/how-it-works"
 />
 <section data-testid="hiw-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-12">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> How it works</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 From first booking to lasting support, <span className="italic text-sage-600">a clear path, every step.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
 We've stripped away everything that makes mental health care feel intimidating, so you can focus on what matters: feeling better.
 </p>
 </div>
 <div className="lg:col-span-5">
 <div className="relative">
 <img
 src="/how-it-works-hero.png"
 alt="How It Works, getting the care you deserve is simple. Compassionate support, every step of the way."
 className="rounded-3xl w-full h-auto object-contain shadow-[0_30px_60px_-30px_rgba(26,43,51,0.3)] bg-cream"
 loading="lazy"
 />
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 </div>
 </div>
 </div>
 </section>

 <section data-testid="hiw-journey" className="container-pad max-w-7xl mx-auto pb-16">
 <div className="space-y-6">
 {journey.map((j, i) => (
 <article key={j.n} data-testid={`journey-${j.n}`} className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start card-soft p-8 lg:p-10">
 <div className="lg:col-span-3 flex lg:flex-col items-center lg:items-start gap-4">
 <div className="w-14 h-14 rounded-2xl bg-gold-500/15 text-gold-600 flex items-center justify-center">
 <j.icon className="w-7 h-7" strokeWidth={1.5} />
 </div>
 <div className="font-heading text-4xl sm:text-5xl text-sage-700/30 tracking-tight">{j.n}</div>
 </div>
 <div className="lg:col-span-9">
 <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-ink">{j.title}</h2>
 <p className="mt-4 text-muted_text leading-relaxed">{j.desc}</p>
 <div className="mt-5 border-l-2 border-sage-500/50 pl-5 text-sm text-ink/80 leading-relaxed">
 {j.detail}
 </div>
 </div>
 </article>
 ))}
 </div>
 </section>

 <section data-testid="hiw-safety" className="bg-paper border-y border-[#E8EBE4]">
 <div className="container-pad max-w-7xl mx-auto py-16 grid lg:grid-cols-12 gap-10 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Privacy & safety</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
 Your information is protected. Your care is private.
 </h2>
 <p className="mt-4 text-muted_text leading-relaxed max-w-2xl">
 We use HIPAA-aligned, encrypted video and messaging. Notes are accessible only to your care team. If you're ever in crisis, we'll provide direct guidance and connect you to immediate help.
 </p>
 <div className="mt-6 inline-flex items-center gap-3 text-sm text-ink">
 <Phone className="w-4 h-4 text-gold-600" strokeWidth={1.75} />
 In emergencies, call 988 (Suicide & Crisis Lifeline) or 911.
 </div>
 </div>
 <div className="lg:col-span-5">
 <div className="card-soft p-7 flex items-start gap-4">
 <div className="w-12 h-12 rounded-2xl bg-sage-500/15 flex items-center justify-center text-sage-600">
 <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
 </div>
 <div>
 <p className="font-heading font-semibold text-ink">Encrypted & confidential</p>
 <p className="mt-2 text-sm text-muted_text leading-relaxed">All visits and messages are encrypted end-to-end. Only your clinician and care coordinator have access to your records.</p>
 </div>
 </div>
 </div>
 </div>
 </section>

 <CTABanner headline="Take the first step today." sub="It can take less than five minutes to schedule your first appointment." />
 </SiteLayout>
 );
}
